import { useState, useEffect, Fragment } from "react";
import { ClipboardCheck, Play, Pause, RotateCcw, Download, CheckCircle2, ChevronDown, ChevronRight, Search, CalendarDays, Save } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, money } from "../lib/api";
import useAutoRefresh from "../lib/useAutoRefresh";

// CSV download for an audit (opened in Excel, ₹-free numbers stay numbers).
const downloadCsv = (filename, rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = rows.map((r) => r.map(esc).join(",")).join("\r\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// Signed rupee for a variance value: +₹295,592.56 found / −₹197,593.27 short.
// money() renders a negative as "₹-197,593.27", which reads wrong next to a
// column of gains — the sign belongs before the ₹.
const moneySigned = (n) => {
  const v = Math.round((Number(n) || 0) * 100) / 100;
  return `${v > 0 ? "+" : v < 0 ? "−" : ""}${money(Math.abs(v))}`;
};

// Stock History (and therefore a back-dated audit) starts at the frozen
// 21 Aug 2026 opening sheet — must stay in step with the backend anchor.
const ANCHOR = "2026-08-21";

// "Today" in salon time. The audit date guard must use IST, not the browser's
// clock, or a late-evening count could be rejected as "in the future".
const istToday = () =>
  new Date().toLocaleDateString("sv-SE", { timeZone: "Asia/Kolkata" });

export default function StockAuditPage() {
  const [history, setHistory] = useState([]);
  const [auditSearch, setAuditSearch] = useState("");
  // Clicking a completed audit opens its saved counts: the history payload
  // already carries every product's expected / actual / variance, the screen
  // simply never drew it (owner report 20 Oct 2026).
  const [expandedAudit, setExpandedAudit] = useState(null);
  const [expandedFilter, setExpandedFilter] = useState("All");
  const [expandedSearch, setExpandedSearch] = useState("");
  const searchedHistory = auditSearch.trim()
    ? history.filter((h) => {
        const q = auditSearch.trim().toLowerCase();
        return (
          (h.audit_date || "").toLowerCase().includes(q) ||
          (h.audited_by || "").toLowerCase().includes(q) ||
          (h.id || "").toLowerCase().startsWith(q) ||
          (h.status || "").toLowerCase().includes(q)
        );
      })
    : history;
  // Abandoned sessions (started, nothing counted) used to sit here as empty
  // "in_progress" rows — they are not audits, so they are not listed.
  const visibleHistory = searchedHistory.filter(
    (h) => h.status === "completed" || (h.total_skus_audited || 0) > 0
  );
  const [skus, setSkus] = useState([]);
  const [activeAudit, setActiveAudit] = useState(null);
  // Expected quantities frozen when a session was paused, so resuming counts
  // against the same baseline instead of whatever stock is now.
  const [pausedExpected, setPausedExpected] = useState({});
  const [actualCounts, setActualCounts] = useState({});
  const [discrepancies, setDiscrepancies] = useState({});
  // Find a product fast while counting: search by name / code / brand and
  // narrow to one category. Filtering only narrows the rows on screen — the
  // audit still saves EVERY product in the session (for a back-dated one,
  // every product Stock History knew on that day), so a hidden row is never
  // dropped.
  const [countSearch, setCountSearch] = useState("");
  const [countCategory, setCountCategory] = useState("All");
  // Owner, 30 Oct 2026: narrow the counting table to one side of the
  // discrepancy while auditing — "Negative" (short, found less than expected)
  // or "Positive" (surplus, found more). "All" shows every row as before.
  const [varianceFilter, setVarianceFilter] = useState("All");
  const [loading, setLoading] = useState(false);
  // Which day the session is counting. Defaults to today; the owner may pick a
  // past day (up to the 21 Aug opening sheet) for a back-dated audit.
  const [auditDate, setAuditDate] = useState(istToday);
  // For a back-dated audit the "System Expected" column cannot be live stock —
  // it must be what Stock History says was on the shelf that day (opening sheet
  // + ledger to that date). sku_id -> closing qty at the audit date.
  const [baseline, setBaseline] = useState({});

  const activeDate = (activeAudit?.audit_date || "").slice(0, 10);
  const isBackDated = Boolean(activeDate && activeDate < istToday());

  // Expected quantity for one product: a paused count freezes it, a
  // back-dated session reads history, a today session reads live stock.
  const expectedFor = (s) => {
    if (pausedExpected[s.id] !== undefined) return pausedExpected[s.id];
    if (isBackDated) return baseline[s.id] ?? 0;
    return s.store_qty || 0;
  };

  // Closing balance of every product on `date`, straight from Stock History —
  // the same opening-sheet-plus-ledger maths the History page shows. Returns
  // the map as well as storing it, because the caller seeds the count inputs
  // from it in the same tick (React state would not be readable yet).
  const fetchBaseline = async (date) => {
    if (!date || date >= istToday()) { setBaseline({}); return {}; }
    try {
      const res = await api.get(`/inventory/history?date_from=${date}&date_to=${date}`);
      const map = {};
      (res.data?.rows || []).forEach((r) => {
        if (r.sku_id) map[r.sku_id] = r.closing;
      });
      setBaseline(map);
      return map;
    } catch (e) {
      console.error(e);
      setBaseline({});
      return {};
    }
  };

  const countQuery = countSearch.trim().toLowerCase();
  // Products that belong in this session. A back-dated audit only counts what
  // Stock History knows was on the shelf that day: a product first created
  // AFTER the audit date has an Expected of 0, and counting it would add the
  // whole live quantity again on top of what history already carries.
  const auditSkus = isBackDated
    ? skus.filter((s) => baseline[s.id] !== undefined)
    : skus;
  const countCategories = ["All", ...Array.from(
    new Set(auditSkus.map((s) => s.category || "Uncategorized"))
  )];
  const uncountedTotal = auditSkus.reduce(
    (n, s) => n + (actualCounts[s.id] === "" || actualCounts[s.id] === undefined || actualCounts[s.id] === null ? 1 : 0),
    0
  );
  const countedSkus = auditSkus.filter((s) => {
    if (countCategory !== "All" && (s.category || "Uncategorized") !== countCategory) return false;
    // Variance sign / status filter: same actual − expected basis as the row and the
    // live valuation.
    if (varianceFilter !== "All") {
      const typed = actualCounts[s.id];
      const hasCount = typed !== "" && typed !== undefined && typed !== null;
      if (varianceFilter === "Blank") {
        if (hasCount) return false;
      } else if (varianceFilter === "Counted") {
        if (!hasCount) return false;
      } else {
        const n = hasCount ? parseFloat(typed) : NaN;
        if (!Number.isFinite(n)) return false;
        const variance = n - expectedFor(s);
        if (varianceFilter === "Negative" && !(variance < 0)) return false;
        if (varianceFilter === "Positive" && !(variance > 0)) return false;
      }
    }
    if (!countQuery) return true;
    return (
      (s.name || "").toLowerCase().includes(countQuery) ||
      (s.category || "").toLowerCase().includes(countQuery) ||
      (s.product_code || "").toLowerCase().includes(countQuery) ||
      (s.brand || "").toLowerCase().includes(countQuery)
    );
  });
  const hasCountFilters =
    Boolean(countQuery) || countCategory !== "All" || varianceFilter !== "All";

  // Live total of the Actual Count column: only rows the auditor has typed.
  // A blank row is not zero — it falls back to expected (zero variance) when
  // the session is saved or submitted.
  const actualTotal = auditSkus.reduce((sum, s) => {
    const typed = actualCounts[s.id];
    if (typed === "" || typed === undefined || typed === null) return sum;
    const n = parseFloat(typed);
    return Number.isFinite(n) ? sum + n : sum;
  }, 0);
  const actualCountedCount = auditSkus.reduce(
    (n, s) => n + (
      actualCounts[s.id] !== "" && actualCounts[s.id] !== undefined && actualCounts[s.id] !== null ? 1 : 0
    ),
    0
  );

  // Owner, 26 Oct 2026: what the counted variance is WORTH while counting.
  // Same basis as the saved audit — variance × unit cost — split into the two
  // halves an owner acts on: short (loss, negative) and found (surplus,
  // positive). Blank rows are skipped exactly like the Actual column.
  const liveValuation = auditSkus.reduce((acc, s) => {
    const typed = actualCounts[s.id];
    if (typed === "" || typed === undefined || typed === null) return acc;
    const n = parseFloat(typed);
    if (!Number.isFinite(n)) return acc;
    const q = n - expectedFor(s);
    const v = Math.round(q * (Number(s.unit_cost) || 0) * 100) / 100;
    if (q > 0) { acc.pos += v; acc.posQty += q; }
    else if (q < 0) { acc.neg += v; acc.negQty += q; }
    return acc;
  }, { pos: 0, neg: 0, posQty: 0, negQty: 0 });

  useEffect(() => {
    fetchHistory();
    fetchSkus();
    // Mount only: the pollers below keep history and stock fresh afterwards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the history current when a colleague closes a count in another tab.
  // The sku poll is skipped while an audit is OPEN: fetchSkus reseeds
  // actualCounts from the server, which would throw away every physical count
  // typed so far. Neither fetch touches `loading`, so nothing blinks.
  useAutoRefresh(() => {
    fetchHistory();
    if (!activeAudit) fetchSkus();
  }, 30000);

  const fetchHistory = async () => {
    try {
      const res = await api.get("/audit/history");
      setHistory(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchSkus = async (forceCounts = false) => {
    try {
      const res = await api.get("/inventory/skus");
      const data = res.data || [];
      setSkus(data);
      const initialCounts = {};
      data.forEach((s) => {
        // Start every count BLANK. The Expected column already shows the
        // system/live/history number; the auditor must type what is actually
        // on the shelf. A blank row is saved as expected (zero variance), so
        // it can never create a false shortage.
        initialCounts[s.id] = "";
      });
      // Never reseed the counts of an audit that is being resumed: its
      // baseline comes from the paused session, not from live stock.
      setActualCounts((prev) => (!forceCounts && Object.keys(pausedExpected).length ? prev : initialCounts));
    } catch (e) {
      console.error(e);
    }
  };

  const startAudit = async () => {
    setLoading(true);
    try {
      const res = await api.post("/audit/start", { date: auditDate });
      const data = res.data || {};
      if (data.audit_id) {
        const day = (data.audit_date || auditDate || "").slice(0, 10);
        setAuditDate(day);
        setPausedExpected({});
        setDiscrepancies({});
        setActiveAudit(data);
        // A past-dated audit counts against what History says was on the shelf
        // that day, not against today's live stock.
        await fetchBaseline(day);
        await fetchSkus(true);
        toast.success(
          day < istToday()
            ? `Started back-dated audit for ${day} — Expected comes from Stock History`
            : `Started audit session: ${data.audit_id.slice(0, 8)}`
        );
      } else {
        toast.error("Failed to start audit");
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const buildItems = () => {
    // Products currently visible on screen (narrowed by the search/category
    // filter). Everything else is "hidden" for this save.
    const visible = new Set(countedSkus.map((s) => s.id));
    return auditSkus.map((s) => {
      const expected = expectedFor(s);
      const typed = actualCounts[s.id];
      const counted = typed !== "" && typed !== undefined && typed !== null;
      // A row hidden by the filter was never seen by the auditor. If it has no
      // typed count it falls back to expected (zero variance) — same as an
      // untouched visible row — but it is flagged in notes so the saved audit
      // is honest about which products were actually counted on screen. The
      // "Not counted" marker also keeps the Actual Count input blank when a
      // paused session is resumed.
      const hidden = !visible.has(s.id);
      const notCountedNote = hidden
        ? "Not counted — product was hidden by the filter"
        : "Not counted — no physical count entered";
      return {
        sku_id: s.id,
        expected_qty: expected,
        actual_qty: counted ? parseFloat(typed) || 0 : expected,
        discrepancy_reason: discrepancies[s.id] || null,
        notes: counted ? "" : notCountedNote,
      };
    });
  };

  const pauseAudit = async () => {
    if (!activeAudit) return;
    setLoading(true);
    try {
      await api.put(`/audit/${activeAudit.audit_id}/items`, { items: buildItems() });
      await api.post(`/audit/${activeAudit.audit_id}/pause`);
      toast.success("Audit paused — every count is saved. Resume it from the history below.");
      setActiveAudit(null);
      setPausedExpected({});
      fetchHistory();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const resumeAudit = async (h) => {
    setLoading(true);
    try {
      const res = await api.post(`/audit/${h.id}/resume`);
      const audit = res.data?.audit || h;
      const items = audit.items || [];
      const day = (audit.audit_date || h.audit_date || "").slice(0, 10);
      // A back-dated session still reads its Expected column from that day's
      // history, even across a pause.
      await fetchBaseline(day);
      const counts = {};
      const reasons = {};
      const frozen = {};
      items.forEach((it) => {
        // Rows saved without a physical count keep their blank input; their
        // stored actual equals expected only so the audit has a complete
        // zero-variance snapshot.
        const notCounted = String(it.notes || "").startsWith("Not counted");
        counts[it.sku_id] = notCounted ? "" : it.actual_qty;
        frozen[it.sku_id] = it.expected_qty;
        if (it.discrepancy_reason && !notCounted) reasons[it.sku_id] = it.discrepancy_reason;
      });
      // Products added after the pause have no saved count yet — start them
      // blank, exactly like a fresh session.
      skus.forEach((s) => {
        if (counts[s.id] === undefined) counts[s.id] = "";
      });
      setActualCounts(counts);
      setDiscrepancies(reasons);
      setPausedExpected(frozen);
      setActiveAudit({ audit_id: h.id, audit_date: h.audit_date });
      setExpandedAudit(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success(`Resumed the ${h.audit_date} audit — ${items.length} counts restored`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const exportAudit = (date, items, status) => {
    const sorted = [...items].sort(
      (a, b) => Math.abs(Number(b.variance) || 0) - Math.abs(Number(a.variance) || 0)
    );
    const num = (k) => sorted.reduce((n, i) => n + (Number(i[k]) || 0), 0);
    // Valuation of the variance (owner, 26 Oct 2026): signed rupees per
    // product, with the loss and the gain reported as two totals instead of
    // only the net, which is what hid the loss.
    const valOf = (i) => {
      const stored = Number(i.variance_value);
      if (Number.isFinite(stored) && (stored !== 0 || !(Number(i.variance) || 0))) return stored;
      return Math.round(((Number(i.variance) || 0) * (Number(i.unit_cost) || 0)) * 100) / 100;
    };
    const posVal = sorted.reduce((n, i) => n + (valOf(i) > 0 ? valOf(i) : 0), 0);
    const negVal = sorted.reduce((n, i) => n + (valOf(i) < 0 ? valOf(i) : 0), 0);
    const rows = [
      ["Stock Audit", `Session ${date}${status ? ` - ${status}` : ""}`],
      ["Date", date],
      ["Products", sorted.length],
      ["System Expected", num("expected_qty")],
      ["Actual Counted", num("actual_qty")],
      ["Net Variance", num("variance")],
      ["Positive Variance Value (found)", Math.round(posVal * 100) / 100],
      ["Negative Variance Value (short)", Math.round(negVal * 100) / 100],
      ["Net Variance Value", Math.round((posVal + negVal) * 100) / 100],
      [],
      ["Product", "System Expected", "Actual Count", "Variance", "Unit Cost", "Variance Value", "Reason"],
      ...sorted.map((i) => [
        i.sku_name,
        Number(i.expected_qty) || 0,
        Number(i.actual_qty) || 0,
        Number(i.variance) || 0,
        Number(i.unit_cost) || 0,
        valOf(i),
        (i.discrepancy_reason || "").replace(/_/g, " "),
      ]),
    ];
    downloadCsv(`stock-audit-${date}.csv`, rows);
    toast.success("Audit exported to CSV");
  };

  const exportActiveAudit = () => {
    if (!activeAudit) return;
    exportAudit(
      activeAudit.audit_date || "session",
      buildItems().map((i) => ({
        ...i,
        sku_name: skus.find((s) => s.id === i.sku_id)?.name || "",
        variance: (Number(i.actual_qty) || 0) - (Number(i.expected_qty) || 0),
        // so an in-progress export values its variance exactly like a saved one
        unit_cost: skus.find((s) => s.id === i.sku_id)?.unit_cost || 0,
      })),
      "in progress"
    );
  };

  const saveAuditProgress = async () => {
    if (!activeAudit) return;
    setLoading(true);
    try {
      await api.put(`/audit/${activeAudit.audit_id}/items`, { items: buildItems() });
      toast.success("Counts saved — the audit stays in progress. Submit it when you are finished.");
      fetchHistory();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const submitAuditItems = async () => {
    if (!activeAudit) return;
    const confirmMsg =
      `Finalize and submit Stock Audit for ${activeDate || "today"}?\n\n` +
      `• Positive Variance (+${money(liveValuation.pos)}): Added to Opening Stock\n` +
      `• Negative Variance (−${money(Math.abs(liveValuation.neg))}): Booked as Consumption for ${activeDate || "this month"}\n` +
      `• Net Impact: ${moneySigned(liveValuation.pos + liveValuation.neg)}\n\n` +
      `Are you sure you want to reconcile digital inventory with these physical counts?`;
    if (!window.confirm(confirmMsg)) return;

    setLoading(true);
    const items = buildItems();
    try {
      const res = await api.put(`/audit/${activeAudit.audit_id}/items`, { items });
      const d = res.data || {};
      toast.success(
        `Submitted — value found ${moneySigned(d.total_positive_value)} · short ${moneySigned(d.total_negative_value)} · net ${moneySigned(d.total_variance_value)}`
      );
      await api.post(`/audit/${activeAudit.audit_id}/complete`);
      toast.success(
        isBackDated
          ? `Audit for ${activeDate} completed — positive variance added to Opening, shortage booked as consumption`
          : "Audit completed & inventory reconciled!"
      );
      setActiveAudit(null);
      setPausedExpected({});
      setBaseline({});
      setAuditDate(istToday());
      fetchHistory();
      fetchSkus(true);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 sm:p-10 max-w-7xl mx-auto space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="lss-overline">Physical Audit</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 flex items-center gap-3 mt-1">
            <ClipboardCheck className="w-8 h-8 text-slate-900" /> Stock Audit & Reconciliation
          </h1>
          <p className="text-slate-600 font-medium text-sm mt-1">
            Physical count verification, digital variance calculations, and stock leakage tracking
          </p>
        </div>
        {!activeAudit ? (
          <div className="flex flex-col sm:items-end gap-1.5">
            <div className="flex items-center gap-2">
              <div className="relative">
                <CalendarDays className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="date"
                  value={auditDate}
                  min={ANCHOR}
                  max={istToday()}
                  onChange={(e) => setAuditDate(e.target.value || istToday())}
                  className="lss-input pl-8 pr-2 py-2 text-sm font-semibold"
                  aria-label="Audit date"
                />
              </div>
              <button
                onClick={startAudit}
                disabled={loading}
                className="flex items-center gap-2 bg-slate-900 text-white font-bold px-5 py-2.5 rounded-lg hover:bg-slate-800 transition shadow-sm text-sm"
              >
                <Play className="w-4 h-4 fill-current" /> Start New Audit Session
              </button>
            </div>
            <span className="text-[11px] font-semibold text-slate-500">
              {auditDate < istToday()
                ? `Back-dated: Expected is read from Stock History on ${auditDate}`
                : "Today's audit counts against live stock"}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={pauseAudit}
              disabled={loading}
              title="Pause the session — every count is kept and you can resume it later"
              className="flex items-center gap-2 bg-amber-500 text-slate-950 font-bold px-4 py-2.5 rounded-lg hover:bg-amber-400 transition shadow-sm text-sm"
            >
              <Pause className="w-4 h-4" /> Pause
            </button>
            <button
              onClick={saveAuditProgress}
              disabled={loading}
              title="Save the counts so far — the audit stays open for more counting"
              className="flex items-center gap-2 bg-slate-800 text-white font-bold px-4 py-2.5 rounded-lg hover:bg-slate-900 transition shadow-sm text-sm"
            >
              <Save className="w-4 h-4" /> Save
            </button>
            <button
              onClick={submitAuditItems}
              disabled={loading}
              title="Finalize the audit and apply the variance to live stock"
              className="flex items-center gap-2 bg-emerald-700 text-white font-bold px-5 py-2.5 rounded-lg hover:bg-emerald-800 transition shadow-sm text-sm"
            >
              <CheckCircle2 className="w-4 h-4" /> Submit
            </button>
          </div>
        )}
      </div>

      {/* Active Audit Form */}
      {activeAudit && (
        <div className="lss-card p-6 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200 pb-4">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="lss-badge bg-amber-100 text-amber-900 border border-amber-300 font-bold">Active Session</span>
                <span className="lss-badge bg-slate-100 text-slate-700 border border-slate-300 font-bold flex items-center gap-1">
                  <CalendarDays className="w-3 h-3" /> Counting as at {activeDate || "today"}
                </span>
              </div>
              <h2 className="text-xl font-extrabold text-slate-900 mt-1">Physical Count Input Form</h2>
              {isBackDated && (
                <p className="text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 mt-1.5 inline-block">
                  Back-dated audit — Expected comes from Stock History on {activeDate}, and completing
                  applies the counted <strong>difference</strong> to today's stock.
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={exportActiveAudit}
                className="flex items-center gap-1.5 bg-white border border-slate-300 text-slate-800 font-bold px-3 py-1.5 rounded-lg hover:bg-slate-50 transition text-xs"
              >
                <Download className="w-3.5 h-3.5" /> Export CSV
              </button>
              <div className="text-xs font-semibold text-slate-600">Session ID: {activeAudit.audit_id}</div>
            </div>
          </div>
          {/* Live total: the Actual Count column starts blank (owner rule,
              23 Oct 2026), so this is only what the auditor has typed so far. */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
            <div>
              <div className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-700">Actual stock counted</div>
              <div className="text-2xl font-extrabold text-emerald-950 tabular">
                {Number(actualTotal.toFixed(2)).toLocaleString()}
              </div>
            </div>
            <div className="border-l border-emerald-300 pl-4">
              <div className="text-[10px] font-extrabold uppercase tracking-wider text-rose-700">Short — value</div>
              <div className="text-xl font-extrabold text-rose-700 tabular">{moneySigned(liveValuation.neg)}</div>
              <div className="text-[11px] font-bold text-rose-600 tabular -mt-0.5">
                {Number(liveValuation.negQty.toFixed(2)).toLocaleString()} units
              </div>
              <span className="text-[9px] font-bold text-rose-800 bg-rose-100/90 px-1.5 py-0.5 rounded border border-rose-300 inline-block mt-1">
                → Books to Consumption of {activeDate || "month"}
              </span>
            </div>
            <div className="border-l border-emerald-300 pl-4">
              <div className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-700">Found — value</div>
              <div className="text-xl font-extrabold text-emerald-700 tabular">{moneySigned(liveValuation.pos)}</div>
              <div className="text-[11px] font-bold text-emerald-600 tabular -mt-0.5">
                +{Number(liveValuation.posQty.toFixed(2)).toLocaleString()} units
              </div>
              <span className="text-[9px] font-bold text-emerald-800 bg-emerald-100/90 px-1.5 py-0.5 rounded border border-emerald-300 inline-block mt-1">
                → Adds to Opening Stock
              </span>
            </div>
            <div className="border-l border-emerald-300 pl-4">
              <div className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-700">Net variance value</div>
              <div className="text-xl font-extrabold text-slate-900 tabular">
                {moneySigned(liveValuation.pos + liveValuation.neg)}
              </div>
            </div>
            <div className="text-xs font-semibold text-emerald-800">
              <div className="flex items-center gap-2">
                <span>{actualCountedCount} of {auditSkus.length} products counted</span>
                {uncountedTotal > 0 && (
                  <button
                    type="button"
                    onClick={() => setVarianceFilter(varianceFilter === "Blank" ? "All" : "Blank")}
                    className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full border transition cursor-pointer ${
                      varianceFilter === "Blank"
                        ? "bg-amber-600 text-white border-amber-700 shadow-xs"
                        : "bg-amber-100 text-amber-900 border-amber-300 hover:bg-amber-200"
                    }`}
                    title="Click to toggle only blank (uncounted) products"
                  >
                    {uncountedTotal} blank
                  </button>
                )}
              </div>
              <div className="text-[10px] font-medium text-emerald-700 mt-0.5">
                Blank rows are saved as expected (zero variance).
              </div>
            </div>
          </div>
          {/* Find products while counting — the audit still saves every row. */}
          <div className="flex flex-wrap items-center gap-2.5">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={countSearch}
                onChange={(e) => setCountSearch(e.target.value)}
                placeholder="Search product name, code or brand..."
                className="lss-input lss-search w-full text-xs font-semibold"
              />
            </div>
            <select
              value={countCategory}
              onChange={(e) => setCountCategory(e.target.value)}
              className="lss-input w-auto text-xs font-semibold"
            >
              {countCategories.map((c) => (
                <option key={c} value={c}>{c === "All" ? "All categories" : c}</option>
              ))}
            </select>
            <select
              value={varianceFilter}
              onChange={(e) => setVarianceFilter(e.target.value)}
              title="Filter products by count status or variance"
              className="lss-input w-auto text-xs font-semibold"
            >
              <option value="All">All products ({auditSkus.length})</option>
              <option value="Blank">⚪ Blank (uncounted) ({uncountedTotal})</option>
              <option value="Counted">✓ Counted only ({actualCountedCount})</option>
              <option value="Negative">⚠ Negative (short)</option>
              <option value="Positive">▲ Positive (surplus)</option>
            </select>
            <button
              type="button"
              onClick={() => setVarianceFilter(varianceFilter === "Blank" ? "All" : "Blank")}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition flex items-center gap-1.5 cursor-pointer ${
                varianceFilter === "Blank"
                  ? "bg-amber-500 text-white border-amber-600 shadow-sm"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}
              title="Filter to show only blank (uncounted) products"
            >
              <span className={`w-2 h-2 rounded-full ${varianceFilter === "Blank" ? "bg-white" : "bg-amber-400"}`}></span>
              <span>Blank ({uncountedTotal})</span>
            </button>
            {hasCountFilters && (
              <button
                type="button"
                onClick={() => { setCountSearch(""); setCountCategory("All"); setVarianceFilter("All"); }}
                className="text-[10px] font-extrabold uppercase tracking-wider text-rose-600 hover:text-rose-800"
              >
                Clear
              </button>
            )}
            <span className="text-[10px] font-bold text-slate-500 ml-auto tabular">
              {countedSkus.length === auditSkus.length
                ? `${auditSkus.length} products`
                : `Showing ${countedSkus.length} of ${auditSkus.length} products`}
            </span>
          </div>
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="lss-table">
              <thead>
                <tr>
                  <th>SKU Name</th>
                  <th>Category</th>
                  <th>{isBackDated ? `Expected on ${activeDate}` : "System Expected"}</th>
                  <th>Actual Count</th>
                  <th>Variance</th>
                  <th className="text-right">Variance Value ₹</th>
                  <th>Reason (If Discrepancy)</th>
                </tr>
              </thead>
              <tbody>
                {countedSkus.map((s) => {
                  const expected = expectedFor(s);
                  const typed = actualCounts[s.id];
                  const hasCount = typed !== "" && typed !== undefined && typed !== null;
                  const actual = hasCount ? (parseFloat(typed) || 0) : null;
                  const variance = hasCount ? actual - expected : null;
                  // Valuation of this product's variance at its unit cost —
                  // the same figure the saved audit stores per item.
                  const unitCost = Number(s.unit_cost) || 0;
                  const varianceValue = variance === null
                    ? null
                    : Math.round(variance * unitCost * 100) / 100;
                  return (
                    <tr key={s.id}>
                      <td className="font-bold text-slate-900">{s.name}</td>
                      <td className="text-slate-600 font-medium">{s.category}</td>
                      <td className="text-slate-900 font-bold tabular">{expected}</td>
                      <td>
                        <input
                          type="number"
                          value={actualCounts[s.id] ?? ""}
                          onChange={(e) =>
                            setActualCounts({ ...actualCounts, [s.id]: e.target.value })
                          }
                          className="w-24 lss-input py-1 px-2 text-sm font-bold tabular"
                        />
                      </td>
                      <td className={`tabular font-bold ${!hasCount ? "text-slate-400" : variance < 0 ? "text-rose-700" : variance > 0 ? "text-emerald-700" : "text-slate-500"}`}>
                        {hasCount ? (variance > 0 ? `+${variance}` : variance) : "—"}
                      </td>
                      <td className={`text-right tabular font-bold ${!hasCount ? "text-slate-400" : varianceValue < 0 ? "text-rose-700" : varianceValue > 0 ? "text-emerald-700" : "text-slate-500"}`}>
                        {hasCount ? moneySigned(varianceValue) : "—"}
                        {hasCount && (
                          <span className="block text-[10px] font-medium text-slate-400">at {money(unitCost)}/unit</span>
                        )}
                      </td>
                      <td>
                        <select
                          value={discrepancies[s.id] || ""}
                          onChange={(e) => setDiscrepancies({ ...discrepancies, [s.id]: e.target.value })}
                          className="lss-input py-1 px-2 text-xs font-semibold"
                        >
                          <option value="">Select Reason...</option>
                          <option value="spill">Spill / Damage</option>
                          <option value="waste">Expired Waste</option>
                          <option value="theft">Unaccounted / Theft</option>
                          <option value="unrecorded_usage">Unrecorded Service Use</option>
                          <option value="count_error">Initial Count Error</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
                {countedSkus.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-slate-500 font-medium">
                      No products match this search. Clear the filter to count every product.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Audit History Log */}
      <div className="lss-card p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h2 className="text-lg font-bold text-slate-900">Audit History</h2>
          <div className="relative w-full sm:w-72">
            <input
              type="text"
              value={auditSearch}
              onChange={(e) => setAuditSearch(e.target.value)}
              placeholder="Search date, auditor, session id…"
              className="lss-input w-full text-xs font-semibold"
            />
          </div>
        </div>
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="lss-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Audited By</th>
                <th>Status</th>
                <th>SKUs Audited</th>
                <th>Total Unit Variance</th>
                <th className="text-right">Value Impact</th>
              </tr>
            </thead>
            <tbody>
              {visibleHistory.map((h) => {
                // Discrepancies first — the reason the audit exists.
                const items = (h.items || [])
                  .slice()
                  .sort((a, b) => Math.abs(Number(b.variance) || 0) - Math.abs(Number(a.variance) || 0));
                const isOpen = expandedAudit === h.id;
                const sum = (k) => items.reduce((n, i) => n + (Number(i[k]) || 0), 0);
                const diffs = items.filter((i) => Number(i.variance) !== 0).length;
                const blankCount = items.filter((i) => String(i.notes || "").startsWith("Not counted")).length;
                const displayedItems = items.filter((it) => {
                  const isUncounted = String(it.notes || "").startsWith("Not counted");
                  const v = Number(it.variance) || 0;
                  if (expandedFilter === "Blank" && !isUncounted) return false;
                  if (expandedFilter === "Counted" && isUncounted) return false;
                  if (expandedFilter === "Diffs" && (v === 0 || isUncounted)) return false;
                  if (expandedSearch) {
                    const q = expandedSearch.toLowerCase();
                    if (!it.sku_name.toLowerCase().includes(q)) return false;
                  }
                  return true;
                });
                // Valuation of this audit's variance (owner, 26 Oct 2026),
                // split by direction: found (positive) vs short (negative).
                // Computed from the rows on screen so the chips can never
                // disagree with the table below them; falls back to the
                // stored totals when a session saved no per-product rows.
                const posVal = items.length
                  ? items.reduce((n, i) => n + (Number(i.variance_value) > 0 ? Number(i.variance_value) : 0), 0)
                  : (Number(h.total_positive_value) || 0);
                const negVal = items.length
                  ? items.reduce((n, i) => n + (Number(i.variance_value) < 0 ? Number(i.variance_value) : 0), 0)
                  : (Number(h.total_negative_value) || 0);
                const uncosted = items.filter((i) => Number(i.variance) !== 0 && !(Number(i.unit_cost) > 0)).length;
                return (
                  <Fragment key={h.id}>
                    <tr
                      onClick={() => setExpandedAudit(isOpen ? null : h.id)}
                      className={`cursor-pointer transition ${isOpen ? "bg-amber-50/70" : "hover:bg-slate-50"}`}
                      title="Click to see the actual stock counted for each product"
                    >
                      <td className="font-bold text-slate-900">
                        <span className="inline-flex items-center gap-1.5">
                          {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-amber-700" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                          {h.audit_date}
                        </span>
                      </td>
                      <td className="text-slate-700 font-medium">{h.audited_by}</td>
                      <td>
                        <span className={`lss-badge border font-bold capitalize ${
                          h.status === "completed"
                            ? "bg-emerald-100 text-emerald-900 border-emerald-300"
                            : h.status === "paused"
                            ? "bg-amber-100 text-amber-900 border-amber-300"
                            : "bg-slate-100 text-slate-700 border-slate-300"
                        }`}>
                          {h.status}
                        </span>
                        {(h.status === "paused" || h.status === "in_progress") && (h.items || []).length > 0 && (
                          <button
                            onClick={(e) => { e.stopPropagation(); resumeAudit(h); }}
                            className="ml-2 inline-flex items-center gap-1 bg-slate-900 text-white font-bold px-2.5 py-1 rounded-md hover:bg-slate-800 transition text-[10px] uppercase tracking-wide"
                            title="Continue counting this session"
                          >
                            <RotateCcw className="w-3 h-3" /> Resume
                          </button>
                        )}
                      </td>
                      <td className="text-slate-900 font-bold">{h.total_skus_audited}</td>
                      <td className={`tabular font-bold ${h.total_variance_units < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                        {h.total_variance_units}
                      </td>
                      <td className="text-right tabular font-extrabold text-slate-900">
                        {money(h.total_variance_value || 0)}
                        <span className="block text-[10px] font-bold whitespace-nowrap">
                          <span className="text-emerald-700">{moneySigned(h.total_positive_value || 0)}</span>
                          <span className="text-slate-400"> · </span>
                          <span className="text-rose-700">{moneySigned(h.total_negative_value || 0)}</span>
                        </span>
                      </td>
                      <td className="w-8 text-center text-slate-400">
                        {isOpen ? <ChevronDown className="w-4 h-4 mx-auto text-amber-700" /> : <ChevronRight className="w-4 h-4 mx-auto" />}
                      </td>
                    </tr>

                    {isOpen && (
                      <tr className="bg-slate-50/70">
                        <td colSpan={7} className="p-0">
                          <div className="px-4 py-4 space-y-3">
                            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs font-bold text-slate-700">
                              <span className="uppercase tracking-wider text-slate-500">Actual stock on {h.audit_date}</span>
                              <span>Products: <strong className="text-slate-950">{items.length}</strong></span>
                              <span>System expected: <strong className="text-slate-950 tabular">{sum("expected_qty")}</strong></span>
                              <span>Actual counted: <strong className="text-slate-950 tabular">{sum("actual_qty")}</strong></span>
                              <span>
                                Net variance:{" "}
                                <strong className={`tabular ${sum("variance") < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                                  {sum("variance") > 0 ? `+${sum("variance")}` : sum("variance")}
                                </strong>
                              </span>
                              <span>
                                Value short:{" "}
                                <strong className="tabular text-rose-700">{moneySigned(negVal)}</strong>
                              </span>
                              <span>
                                Value found:{" "}
                                <strong className="tabular text-emerald-700">{moneySigned(posVal)}</strong>
                              </span>
                              <span>
                                Net value:{" "}
                                <strong className="tabular text-slate-950">{moneySigned(posVal + negVal)}</strong>
                              </span>
                              {uncosted > 0 && (
                                <span
                                  className="text-[10px] font-extrabold uppercase tracking-wider text-amber-700"
                                  title="These products have no unit cost recorded, so their variance is valued at ₹0"
                                >
                                  {uncosted} valued at ₹0 (no cost)
                                </span>
                              )}
                              <span>
                                With a difference:{" "}
                                <strong className={diffs ? "text-rose-700" : "text-slate-950"}>{diffs}</strong>
                              </span>
                              <span className="text-[10px] uppercase tracking-widest text-slate-400">differences shown first</span>
                              <span className="ml-auto flex items-center gap-2">
                                {h.status !== "completed" && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); resumeAudit(h); }}
                                    className="inline-flex items-center gap-1 bg-slate-900 text-white font-bold px-3 py-1.5 rounded-lg hover:bg-slate-800 transition text-[11px]"
                                  >
                                    <RotateCcw className="w-3.5 h-3.5" /> Resume Counting
                                  </button>
                                )}
                                <button
                                  onClick={(e) => { e.stopPropagation(); exportAudit(h.audit_date, items, h.status); }}
                                  className="inline-flex items-center gap-1 bg-white border border-slate-300 text-slate-800 font-bold px-3 py-1.5 rounded-lg hover:bg-slate-100 transition text-[11px]"
                                >
                                  <Download className="w-3.5 h-3.5" /> Export CSV
                                </button>
                              </span>
                            </div>

                            {/* Filter bar for expanded audit details */}
                            <div className="flex flex-wrap items-center gap-2 pt-1 pb-1">
                              <div className="relative flex-1 min-w-[200px]">
                                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                                <input
                                  value={expandedSearch}
                                  onChange={(e) => setExpandedSearch(e.target.value)}
                                  placeholder="Search products in this audit..."
                                  className="lss-input lss-search w-full text-xs py-1 pl-8 font-semibold"
                                />
                              </div>
                              <select
                                value={expandedFilter}
                                onChange={(e) => setExpandedFilter(e.target.value)}
                                className="lss-input w-auto text-xs py-1 font-semibold"
                              >
                                <option value="All">All products ({items.length})</option>
                                <option value="Blank">⚪ Blank (uncounted) ({blankCount})</option>
                                <option value="Counted">✓ Counted only ({items.length - blankCount})</option>
                                <option value="Diffs">⚠ With variance ({diffs})</option>
                              </select>
                              {blankCount > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setExpandedFilter(expandedFilter === "Blank" ? "All" : "Blank")}
                                  className={`px-2.5 py-1 text-xs font-bold rounded-md border transition flex items-center gap-1.5 cursor-pointer ${
                                    expandedFilter === "Blank"
                                      ? "bg-amber-500 text-white border-amber-600 shadow-xs"
                                      : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                                  }`}
                                  title="Filter to show only blank (uncounted) products"
                                >
                                  <span className={`w-2 h-2 rounded-full ${expandedFilter === "Blank" ? "bg-white" : "bg-amber-400"}`}></span>
                                  <span>Blank ({blankCount})</span>
                                </button>
                              )}
                              {(expandedSearch || expandedFilter !== "All") && (
                                <button
                                  type="button"
                                  onClick={() => { setExpandedSearch(""); setExpandedFilter("All"); }}
                                  className="text-[10px] font-extrabold uppercase tracking-wider text-rose-600 hover:text-rose-800"
                                >
                                  Clear
                                </button>
                              )}
                              <span className="text-[11px] font-bold text-slate-500 ml-auto tabular">
                                Showing {displayedItems.length} of {items.length} products
                              </span>
                            </div>

                            <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white max-h-96 overflow-y-auto">
                              <table className="lss-table text-xs">
                                <thead className="sticky top-0 bg-white">
                                  <tr>
                                    <th>Product</th>
                                    <th className="text-right">System Expected</th>
                                    <th className="text-right">Actual Count</th>
                                    <th className="text-right">Variance</th>
                                    <th className="text-right">Unit Cost ₹</th>
                                    <th className="text-right">Variance Value ₹</th>
                                    <th>Reason</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {displayedItems.map((it) => {
                                    const v = Number(it.variance) || 0;
                                    const vv = Number(it.variance_value) || 0;
                                    const isUncounted = String(it.notes || "").startsWith("Not counted");
                                    return (
                                      <tr key={it.id || it.sku_id} className={isUncounted ? "bg-amber-50/40" : v !== 0 ? "bg-rose-50/60" : ""}>
                                        <td className="font-bold text-slate-900">
                                          {it.sku_name}
                                          {isUncounted && (
                                            <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300">
                                              Blank / Uncounted
                                            </span>
                                          )}
                                        </td>
                                        <td className="text-right tabular text-slate-700">{Number(it.expected_qty) || 0}</td>
                                        <td className="text-right tabular font-extrabold text-slate-950">
                                          {isUncounted ? <span className="text-slate-400 font-normal italic">— (blank)</span> : (Number(it.actual_qty) || 0)}
                                        </td>
                                        <td className={`text-right tabular font-bold ${isUncounted ? "text-slate-400" : v < 0 ? "text-rose-700" : v > 0 ? "text-emerald-700" : "text-slate-400"}`}>
                                          {isUncounted ? "—" : v > 0 ? `+${v}` : v}
                                        </td>
                                        <td className="text-right tabular text-slate-600">{money(Number(it.unit_cost) || 0)}</td>
                                        <td className={`text-right tabular font-extrabold ${isUncounted ? "text-slate-400" : vv < 0 ? "text-rose-700" : vv > 0 ? "text-emerald-700" : "text-slate-400"}`}>
                                          {isUncounted ? "—" : moneySigned(vv)}
                                        </td>
                                        <td className="text-slate-600 font-semibold capitalize">
                                          {isUncounted ? (
                                            <span className="text-amber-700 text-[11px] font-medium italic">Not counted</span>
                                          ) : (
                                            (it.discrepancy_reason || "").replace(/_/g, " ") || "—"
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                  {displayedItems.length === 0 && (
                                    <tr>
                                      <td colSpan={7} className="text-center py-5 text-slate-500 font-medium">
                                        No products match the filter.
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {visibleHistory.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-6 text-slate-500 font-medium">
                    {auditSearch.trim()
                      ? `No audits match "${auditSearch.trim()}".`
                      : 'No audits yet. Click "Start New Audit Session" to begin a count.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
